// Tabs/RegistrationSessionDetailsModal.tsx
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Modal, Grid, Divider, Stack, Group, Box, Text, Badge, Loader, Center,
  ScrollArea, Textarea, Checkbox, Button,
} from '@mantine/core';
import { IconSend } from '@tabler/icons-react';
import ChatBubble from '@/components/advanced/communication/ChatBubble';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useRegistrationService,
  useUserService,
  RegistrationSessionModelView,
  RegistrationSessionNoteModelView,
} from '@actschurch/ccms-sdk';
import ConfirmActionModal from '../Tabs/ConfirmActionModal';
import SessionStateBadge from './SessionStateBadge';

interface RegistrationSessionDetailsModalProps {
  opened: boolean;
  onClose: () => void;
  session: RegistrationSessionModelView;
  currentUserId?: string;
}

interface NoteItem {
  id: string;
  noteID: number;
  authorId: string;
  authorName: string;
  message: string;
  timestamp: string;
  isEdited: boolean;
  hideFromFacilitator: boolean;
}

function sdkDateToISO(value: number | string): string {
  const raw = String(value ?? '');
  if (raw.length < 14) return new Date().toISOString();
  return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}T${raw.slice(8, 10)}:${raw.slice(10, 12)}:${raw.slice(12, 14)}`;
}

function getContrastText(hex?: string | null): string {
  if (!hex) return '#fff';
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return '#fff';
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#1A1B1E' : '#fff';
}

export function ColorBadge({ label, color }: { label: string; color?: string | null }) {
  return (
    <Badge
      variant="light"
      style={{
        backgroundColor: color ?? 'var(--mantine-color-gray-5)',
        color: getContrastText(color),
      }}
    >
      {label}
    </Badge>
  );
}

export default function RegistrationSessionDetailsModal({
  opened,
  onClose,
  session,
  currentUserId,
}: RegistrationSessionDetailsModalProps) {
  const registrationService = useRegistrationService();
  const userService         = useUserService();

  const [notes, setNotes] = useState<NoteItem[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);

  const [draft, setDraft] = useState('');
  const [hideFromFacilitator, setHideFromFacilitator] = useState(false);
  const [posting, setPosting] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');

  const [deleteCommentId, setDeleteCommentId] = useState<string | null>(null);
  const [deleteCommentOpen, setDeleteCommentOpen] = useState(false);

  const nameCache = useRef<Map<string, string>>(new Map());

  const resolveName = useCallback(async (userId: string): Promise<string> => {
    if (!userId) return 'Staff';
    if (nameCache.current.has(userId)) return nameCache.current.get(userId)!;
    try {
      const res: any = await userService.getById(userId);
      const user = res?.data ?? res;
      if (user) {
        const name =
          [user.firstName, user.lastName ?? user.surname].filter(Boolean).join(' ').trim() ||
          user.displayName?.trim() ||
          user.email ||
          userId;
        nameCache.current.set(userId, name);
        return name;
      }
    } catch {
      // fall through
    }
    nameCache.current.set(userId, userId);
    return userId;
  }, [userService]);

  const noteToItem = useCallback(async (note: RegistrationSessionNoteModelView): Promise<NoteItem> => {
    // Admin author (adminUserId) vs facilitator author (userId) — resolve
    // whichever is present so multiple admins/facilitators are distinguishable.
    const authorId = note.adminUserId ?? note.userId ?? note.authorUserId ?? '';
    let authorName = note.authorName?.trim() || 'Staff';
    if (!note.authorName) {
      if (note.adminUserId) {
        authorName = await resolveName(note.adminUserId);
      } else if (note.userId) {
        authorName = await resolveName(note.userId);
      }
    }
    return {
      id: String(note.sessionNoteID),
      noteID: note.sessionNoteID,
      authorId,
      authorName,
      message: note.note ?? '',
      timestamp: sdkDateToISO(note.entryDateTime),
      isEdited: !!note.modifiedDateTime && note.modifiedDateTime !== note.createdDateTime,
      hideFromFacilitator: note.hideFromFacilitator,
    };
  }, [resolveName]);

  const fetchNotes = useCallback(async () => {
    if (!session.registrationSessionID) return;
    setLoadingNotes(true);
    try {
      const res: any = await registrationService.getSessionNotes(session.registrationSessionID);
      const raw: RegistrationSessionNoteModelView[] = res?.data ?? res ?? [];
      const sorted = [...raw].sort((a, b) => a.entryDateTime - b.entryDateTime);
      const resolved = await Promise.all(sorted.map(noteToItem));
      setNotes(resolved);
    } catch (e) {
      console.error('Error fetching session notes:', e);
      toast.error('Error', 'Could not load notes.');
    } finally {
      setLoadingNotes(false);
    }
  }, [registrationService, session.registrationSessionID, noteToItem]);

  useEffect(() => { if (opened) fetchNotes(); }, [opened, fetchNotes]);

  const handlePost = async () => {
    const content = draft.trim();
    if (!content) return;
    setPosting(true);
    try {
      await registrationService.addSessionNote(session.registrationSessionID, {
        registrationSessionID: session.registrationSessionID,
        note:                  content,
        hideFromFacilitator,
      });
      setDraft('');
      setHideFromFacilitator(false);
      await fetchNotes();
      toast.success('Note Added', 'Your note has been saved.');
    } catch (e) {
      console.error('Error adding session note:', e);
      toast.error('Error', 'Could not save note. Please try again.');
    } finally {
      setPosting(false);
    }
  };

  const startEdit = (item: NoteItem) => {
    setEditingId(item.id);
    setEditDraft(item.message);
  };

  const handleSaveEdit = async (item: NoteItem) => {
    const content = editDraft.trim();
    if (!content) return;
    try {
      await registrationService.updateSessionNote(session.registrationSessionID, item.noteID, { note: content });
      setEditingId(null);
      setEditDraft('');
      await fetchNotes();
      toast.success('Note Updated', 'Your note has been updated.');
    } catch (e) {
      console.error('Error updating session note:', e);
      toast.error('Error', 'Could not update note. Please try again.');
    }
  };

  const handleDeleteComment = (id: string) => {
    setDeleteCommentId(id);
    setDeleteCommentOpen(true);
  };

  const handleConfirmDeleteComment = async () => {
    const id = deleteCommentId;
    if (!id) {
      setDeleteCommentOpen(false);
      return;
    }
    const noteId = Number(id);
    if (!noteId) {
      setDeleteCommentOpen(false);
      setDeleteCommentId(null);
      return;
    }

    try {
      const ok = await registrationService.deleteSessionNote(session.registrationSessionID, noteId);
      if (ok) {
        toast.warning('Note Deleted', 'The note has been deleted.');
        await fetchNotes();
      } else {
        toast.error('Error', 'Could not delete note.');
      }
    } catch (e) {
      console.error('Error deleting session note:', e);
      toast.error('Error', 'Could not delete note. Please try again.');
    } finally {
      setDeleteCommentOpen(false);
      setDeleteCommentId(null);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={session.name ?? 'Session Details'}
      size="90%"
      centered
    >
      <Grid gutter="xl">
        {/* ── Left: session details ────────────────────────────────────── */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Group justify="space-between" mb="md">
            <Text size="sm" fw={600}>Session details</Text>
          </Group>
          <Divider mb="md" />
          <Stack gap="md">
            <Group gap="xs">
              <SessionStateBadge session={session} kind="progress" />
              <SessionStateBadge session={session} kind="status" />
              {session.isSpecial && <Badge variant="light" color="grape">Special</Badge>}
            </Group>

            <Box>
              <Text size="xs" c="dimmed">Date</Text>
              <Text size="sm">{session.sessionDateDisplay ?? '—'}</Text>
            </Box>

            {session.description && (
              <Box>
                <Text size="xs" c="dimmed">Description</Text>
                <Text size="sm">{session.description}</Text>
              </Box>
            )}

            {session.details && (
              <Box>
                <Text size="xs" c="dimmed">Details</Text>
                <Text size="sm">{session.details}</Text>
              </Box>
            )}

            {session.notes && (
              <Box>
                <Text size="xs" c="dimmed">Facilitator / interview notes</Text>
                <Text size="sm">{session.notes}</Text>
              </Box>
            )}

          </Stack>
        </Grid.Col>

        {/* ── Right: notes ─────────────────────────────────────────────── */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Divider
            label={<Text size="sm" fw={600}>Notes</Text>}
            labelPosition="center"
            mb="md"
            mt={28}
          />

          {loadingNotes ? (
            <Center py="md">
              <Loader size="sm" />
            </Center>
          ) : (
            <Stack gap="md">
              <ScrollArea.Autosize mah={420} type="auto">
                <Stack gap={4} py="xs">
                  {notes.length === 0 && (
                    <Center py="lg">
                      <Text size="sm" c="dimmed">No notes yet.</Text>
                    </Center>
                  )}
                  {notes.map((item) => {
                    const isOwn = !!currentUserId && item.authorId === currentUserId;

                    if (editingId === item.id) {
                      return (
                        <Box key={item.id} maw="70%" ml={isOwn ? 'auto' : 0} mr={isOwn ? 0 : 'auto'}>
                          <Textarea
                            value={editDraft}
                            onChange={(e) => setEditDraft(e.currentTarget.value)}
                            autosize
                            minRows={2}
                          />
                          <Group gap="xs" justify="flex-end" mt={4}>
                            <Button size="xs" variant="subtle" onClick={() => setEditingId(null)}>
                              Cancel
                            </Button>
                            <Button size="xs" onClick={() => handleSaveEdit(item)}>
                              Save
                            </Button>
                          </Group>
                        </Box>
                      );
                    }

                    return (
                      <Box key={item.id} maw="70%" ml={isOwn ? 'auto' : 0} mr={isOwn ? 0 : 'auto'}>
                        <Text size="xs" fw={600} ta={isOwn ? 'right' : 'left'} mb={2} px={4}>
                          {item.authorName}
                          {item.hideFromFacilitator && (
                            <Text component="span" size="xs" c="dimmed" fw={400} ml={6}>
                              (hidden from facilitator)
                            </Text>
                          )}
                        </Text>
                        <ChatBubble
                          message={item.message}
                          sender={{ name: item.authorName }}
                          timestamp={item.timestamp}
                          // Always render the readable/colored bubble style; real
                          // ownership still drives alignment above and edit/delete
                          // permissions below.
                          isOwn
                          isEdited={item.isEdited}
                          showAvatar={false}
                          onEdit={isOwn ? () => startEdit(item) : undefined}
                          onDelete={isOwn ? () => handleDeleteComment(item.id) : undefined}
                          onCopy={() => {
                            navigator.clipboard.writeText(item.message);
                            toast.success('Copied', 'Note copied to clipboard.');
                          }}
                        />
                      </Box>
                    );
                  })}
                </Stack>
              </ScrollArea.Autosize>

              <Stack gap="xs">
                <Textarea
                  placeholder="Write a note…"
                  value={draft}
                  onChange={(e) => setDraft(e.currentTarget.value)}
                  autosize
                  minRows={2}
                  maxRows={6}
                />
                <Group justify="space-between">
                  <Checkbox
                    label="Hide from facilitator"
                    checked={hideFromFacilitator}
                    onChange={(e) => setHideFromFacilitator(e.currentTarget.checked)}
                  />
                  <Button
                    leftSection={<IconSend size={14} />}
                    onClick={handlePost}
                    loading={posting}
                    disabled={!draft.trim()}
                  >
                    Post
                  </Button>
                </Group>
              </Stack>
            </Stack>
          )}
        </Grid.Col>
      </Grid>
      <ConfirmActionModal
        opened={deleteCommentOpen}
        onClose={() => setDeleteCommentOpen(false)}
        title="Delete note"
        message="Delete this note? This cannot be undone."
        color="red"
        onConfirm={handleConfirmDeleteComment}
      />
    </Modal>
  );
}